import { useCallback } from "react";
import { ReschedulingRequestDto } from "../api-client";
import useStore from "../helpers/useStore";
import { MutationDraftContext } from "../stores/MutationDraft/mutationDraft.provider";
import { AgentChatContext } from "../stores/AgentChat/agentChat.provider";
import { AppointmentContext } from "../stores/Appointment/appointment.provider";

/**
 * Reusable hook that encapsulates the rescheduling-agent workflow:
 * clears previous state, fires the API call, and updates draft + chat stores.
 */
export const useReschedulingAgent = (patientId: number | string | undefined) => {
  const mutationStoreRegistry = useStore(MutationDraftContext);
  const chatStoreRegistry = useStore(AgentChatContext);
  const appointmentStore = useStore(AppointmentContext);

  const draftStore = mutationStoreRegistry.getDefaultStore();
  const chatStore = chatStoreRegistry.getDefaultStore();

  const startRescheduling = useCallback(
    async (userInstructions?: string) => {
      if (!patientId) return;

      const locationId = appointmentStore.userStore.user?.locationId as string;
      const request: ReschedulingRequestDto = {
        patientId: patientId.toString(),
        locationId,
        userInstructions,
      };

      // Clear previous drafts, rejections, and chat for a fresh start
      draftStore.clearAll();
      chatStore.clear();

      draftStore.agent.setRunning("rescheduling", request);

      chatStore.add({
        role: "assistant",
        content: "Rescheduling-Agent gestartet — Termine werden neu berechnet…",
        timestamp: new Date(),
        isLoading: true,
      });

      try {
        console.log("Starting rescheduling workflow with request:", request);
        const job = await appointmentStore.runRescheduling(request);
        if (job) {
          draftStore.agent.setWorkflow(job);
        } else {
          draftStore.agent.setError("Workflow konnte nicht gestartet werden.");
          chatStore.removeLoading();
          chatStore.add({
            role: "system",
            content: "Fehler: Workflow konnte nicht gestartet werden.",
            timestamp: new Date(),
          });
        }
      } catch (err: any) {
        const msg = err?.response?.data?.message || err?.message || "Unbekannter Fehler";
        draftStore.agent.setError(msg);
        chatStore.removeLoading();
        chatStore.add({
          role: "system",
          content: `Fehler: ${msg}`,
          timestamp: new Date(),
        });
      }
    },
    [patientId, appointmentStore, draftStore, chatStore],
  );

  return { startRescheduling };
};
